import { apiClient } from './client';
import axios from 'axios';
import { useAuthStore } from '@/store/authStore';

export interface UploadedMedia {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  thumbnailUrl: string;
  expiresAt: string;
}

// Клиентские лимиты (должны ≤ серверным env.MAX_MEDIA_SIZE_MB).
// Если сервер отдаёт лимит в /api/auth/me или ENV-bootstrap — предпочтём его.
// Пока держим константой; /media/upload всё равно перепроверяет на сервере.
export const MEDIA_LIMITS = {
  maxImageMB: 20,
  maxVideoMB: 300,      // TikTok-class: 300 МБ как по ТЗ
  maxAudioMB: 50,
  maxVideoDurationSec: 600,
};

export class MediaValidationError extends Error {
  code: 'TOO_BIG' | 'BAD_TYPE' | 'TOO_LONG';
  constructor(code: MediaValidationError['code'], message: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Pre-flight validation: размер + тип + (для видео) длительность.
 * Кидает MediaValidationError до того, как начнётся реальный upload —
 * чтобы юзер не ждал 5 минут из-за файла, который сервер всё равно отрежет.
 */
export async function validateMediaBeforeUpload(file: File): Promise<void> {
  const mb = file.size / 1024 / 1024;

  // iOS часто шлёт HEIC с пустым type или application/octet-stream.
  // Распознаём по расширению как фолбэк.
  const extIsImage = /\.(jpe?g|png|webp|gif|heic|heif|avif|bmp|tiff?)$/i.test(file.name);
  const extIsVideo = /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(file.name);
  const extIsAudio = /\.(mp3|m4a|aac|ogg|wav|flac|opus)$/i.test(file.name);

  if (file.type.startsWith('image/') || (!file.type && extIsImage) || (file.type === 'application/octet-stream' && extIsImage)) {
    if (mb > MEDIA_LIMITS.maxImageMB) {
      throw new MediaValidationError('TOO_BIG', `Картинка ${mb.toFixed(1)} МБ — максимум ${MEDIA_LIMITS.maxImageMB} МБ`);
    }
  } else if (file.type.startsWith('video/') || (!file.type && extIsVideo)) {
    if (mb > MEDIA_LIMITS.maxVideoMB) {
      throw new MediaValidationError('TOO_BIG', `Видео ${mb.toFixed(1)} МБ — максимум ${MEDIA_LIMITS.maxVideoMB} МБ`);
    }
    // Читаем метаданные чтобы узнать длительность без полной загрузки.
    const duration = await probeVideoDuration(file).catch(() => null);
    if (duration != null && duration > MEDIA_LIMITS.maxVideoDurationSec) {
      throw new MediaValidationError(
        'TOO_LONG',
        `Видео ${Math.round(duration)}с — максимум ${MEDIA_LIMITS.maxVideoDurationSec}с (${Math.round(MEDIA_LIMITS.maxVideoDurationSec / 60)} мин)`,
      );
    }
  } else if (file.type.startsWith('audio/') || (!file.type && extIsAudio)) {
    if (mb > MEDIA_LIMITS.maxAudioMB) {
      throw new MediaValidationError('TOO_BIG', `Аудио ${mb.toFixed(1)} МБ — максимум ${MEDIA_LIMITS.maxAudioMB} МБ`);
    }
  } else {
    throw new MediaValidationError('BAD_TYPE', `Только фото, видео и аудио (тип: ${file.type || 'неизвестен'})`);
  }
}

function probeVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(v.duration);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Не удалось прочитать метаданные видео'));
    };
    v.src = url;
  });
}

export const mediaApi = {
  async upload(
    file: File,
    opts?: {
      channelId?: string;
      recipientId?: string;
      onProgress?: (percent: number) => void;
      signal?: AbortSignal;
    },
  ): Promise<UploadedMedia> {
    // Pre-flight — бросает если файл невалиден.
    await validateMediaBeforeUpload(file);

    const form = new FormData();
    form.append('file', file);
    if (opts?.channelId) form.append('channelId', opts.channelId);
    if (opts?.recipientId) form.append('recipientId', opts.recipientId);

    // Upload должен идти без 60s timeout (большое видео на мобильной сети
    // может качаться 10+ минут).  Используем тот же apiClient чтобы сохранить
    // auth/refresh interceptor'ы, но переопределяем timeout=0 и добавляем
    // onUploadProgress / signal для прогресса и отмены.
    try {
      const res = await apiClient.post<UploadedMedia>('/api/media/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        signal: opts?.signal,
        onUploadProgress: (e) => {
          if (!opts?.onProgress || !e.total) return;
          opts.onProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      return res.data;
    } catch (e) {
      // Нормализуем сетевые ошибки в читаемые сообщения.
      if (axios.isAxiosError(e)) {
        const status = e.response?.status;
        if (status === 413) {
          throw new MediaValidationError('TOO_BIG', 'Сервер отказал: файл слишком большой');
        }
        if (e.code === 'ECONNABORTED') {
          throw new Error('Соединение прервано. Проверь интернет.');
        }
      }
      throw e;
    }
  },

  thumbnailUrl(mediaFileId: string): string {
    // <img> tags can't send Authorization headers, so we encode the access
    // token as a query parameter (the backend's optionalAuth middleware
    // accepts both `Bearer <jwt>` and `?token=<jwt>`).
    //
    // ⚠️ DEPRECATED — используется только для backward-compat в местах, где
    // ещё не прошёл рефактор на signed URLs (`useSignedMediaUrl` hook ниже).
    // JWT-в-query утекает в nginx access.log и Referer header — поэтому новые
    // компоненты должны использовать signed URLs.
    const t = useAuthStore.getState().accessToken;
    return `/api/media/${mediaFileId}/thumbnail${t ? `?token=${t}` : ''}`;
  },

  downloadUrl(mediaFileId: string): string {
    // ⚠️ DEPRECATED — см. thumbnailUrl().
    const t = useAuthStore.getState().accessToken;
    return `/api/media/${mediaFileId}/download${t ? `?token=${t}` : ''}`;
  },

  /**
   * Запрашивает batch signed URLs у бэкенда. Возвращает map
   * `{[mediaId]: { thumbnail?, download?, raw? }}`. Каждый URL живёт
   * `MEDIA_URL_TTL_SECONDS` (по умолчанию 5 мин) — caller должен кешировать
   * на 4 мин и refetch'ить ближе к expire.
   */
  async signMedia(
    mediaIds: string[],
    types: ('thumbnail' | 'download' | 'raw' | 'playlist.m3u8')[],
  ): Promise<Record<string, Record<string, string>>> {
    if (mediaIds.length === 0) return {};
    const res = await apiClient.post<{ urls: Record<string, Record<string, string>> }>(
      '/api/media/sign',
      { mediaIds, types },
    );
    return res.data.urls ?? {};
  },
};
